/**
 * `/answer/questionnaires/:id` — questionnaire detail + review UI (M-ANSWER.3).
 *
 * Three panels:
 *   1. Header — metadata, status badge, /generate kick-off, export CSV link, delete.
 *   2. Import — bulk-paste questions (one per line; section/external_id parsed
 *      from `[section] external_id: question_text` shorthand). Only available
 *      in `draft` status.
 *   3. Questions list — each question with optional response inline. When a
 *      response exists, operators can edit final_text, flip review_status
 *      (pending → approved | edited | rejected), and promote to the library.
 *
 * The hydrated detail endpoint (#103) returns everything in one round-trip
 * including citations; the page polls at 3s while status === "generating"
 * so newly-drafted responses land live without refresh.
 */
import type { AnswerQuestionWithResponse } from "@simpliwork/api-schema";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAnswerQuestionnaire,
  useDeleteAnswerQuestionnaire,
  useGenerateAnswerQuestionnaire,
  useImportAnswerQuestions,
  usePromoteAnswerLibrary,
  useReviewAnswerResponse,
} from "@/hooks/use-answer";
import { answerExportUrl } from "@/lib/answer-api";
import { formatTimestamp } from "@/lib/formatters";

type ImportInputRow = { external_id?: string; section?: string; question_text: string };

/**
 * Parse the bulk-paste textarea. One line per question. Supports two
 * shorthands so operators can paste a CSV-ish dump.
 */
function parseBulk(text: string): ImportInputRow[] {
  const rows: ImportInputRow[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    let section: string | undefined;
    let rest = line;
    const sectionMatch = /^\[([^\]]+)\]\s*(.*)$/.exec(rest);
    if (sectionMatch) {
      section = sectionMatch[1]!.trim();
      rest = sectionMatch[2]!.trim();
    }
    let externalId: string | undefined;
    const idMatch = /^([A-Za-z0-9._-]+):\s*(.+)$/.exec(rest);
    if (idMatch) {
      externalId = idMatch[1]!.trim();
      rest = idMatch[2]!.trim();
    }
    if (!rest) continue;
    rows.push({
      ...(externalId ? { external_id: externalId } : {}),
      ...(section ? { section } : {}),
      question_text: rest,
    });
  }
  return rows;
}

export function AnswerQuestionnaireDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const q = useAnswerQuestionnaire(id);

  const importMut = useImportAnswerQuestions();
  const generateMut = useGenerateAnswerQuestionnaire();
  const deleteMut = useDeleteAnswerQuestionnaire();

  const [bulk, setBulk] = useState("");

  function onDelete() {
    if (!q.data) return;
    if (!window.confirm(`Delete questionnaire "${q.data.title}"? Cannot be undone.`)) return;
    deleteMut.mutate(q.data.id, {
      onSuccess: () => navigate("/answer/questionnaires"),
    });
  }

  function onImport(e: React.FormEvent) {
    e.preventDefault();
    const questions = parseBulk(bulk);
    if (questions.length === 0) return;
    importMut.mutate(
      { questionnaireId: id, body: { questions } },
      {
        onSuccess: () => setBulk(""),
      },
    );
  }

  function onGenerate() {
    if (!q.data) return;
    const questionCount = q.data.questions.length;
    if (questionCount === 0) return;
    if (
      !window.confirm(
        `Generate draft answers for ${questionCount} questions? Estimated cost ~ ${questionCount * 2} credits.`,
      )
    ) {
      return;
    }
    generateMut.mutate(q.data.id);
  }

  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Answer</p>
        <h1
          className="text-2xl font-extrabold tracking-tight text-gray-900"
          data-testid="answer-q-detail-heading"
        >
          Questionnaire detail
        </h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          <button
            type="button"
            className="underline-offset-2 hover:underline"
            onClick={() => navigate("/answer/questionnaires")}
            data-testid="answer-q-detail-back-link"
          >
            Back to questionnaires
          </button>
        </p>
      </header>

      {q.isLoading && <Skeleton className="h-32 w-full" data-testid="answer-q-detail-skeleton" />}

      {q.error && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive" data-testid="answer-q-detail-error">
              Couldn't load questionnaire: {q.error.message}
            </p>
          </CardContent>
        </Card>
      )}

      {q.data && (
        <>
          <Card data-testid="answer-q-detail-meta-card">
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle data-testid="answer-q-detail-title">{q.data.title}</CardTitle>
                  <CardDescription>
                    {q.data.buyer_name ? `${q.data.buyer_name} · ` : ""}
                    created {formatTimestamp(q.data.created_at)}
                  </CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    variant={
                      q.data.status === "ready" || q.data.status === "completed"
                        ? "default"
                        : "outline"
                    }
                    data-testid="answer-q-detail-status"
                  >
                    {q.data.status}
                  </Badge>
                  {q.data.status === "draft" && q.data.questions.length > 0 && (
                    <Button
                      size="sm"
                      onClick={onGenerate}
                      disabled={generateMut.isPending}
                      data-testid="answer-q-detail-generate-cta"
                    >
                      {generateMut.isPending ? "Generating..." : "Generate drafts"}
                    </Button>
                  )}
                  {(q.data.status === "ready" || q.data.status === "completed") && (
                    <a href={answerExportUrl(q.data.id)} download>
                      <Button variant="outline" size="sm">
                        Export CSV
                      </Button>
                    </a>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onDelete}
                    disabled={deleteMut.isPending}
                    data-testid="answer-q-detail-delete-cta"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </CardHeader>
            {generateMut.error && (
              <CardContent className="pt-0">
                <p className="text-sm text-destructive">
                  Generate failed: {generateMut.error.message}
                </p>
              </CardContent>
            )}
          </Card>

          {q.data.status === "draft" && (
            <Card data-testid="answer-q-detail-import-card">
              <CardHeader>
                <CardTitle>Import questions</CardTitle>
                <CardDescription>
                  One per line. Shorthand:{" "}
                  <code className="rounded bg-muted px-1 text-xs">[Section] EXT-001: text</code>{" "}
                  or just <code className="rounded bg-muted px-1 text-xs">text</code>. Max 500
                  per import; existing questions stay (the schema's UNIQUE constraint blocks
                  duplicates on sort_order).
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={onImport} className="flex flex-col gap-3">
                  <textarea
                    className="min-h-[160px] rounded border border-input bg-background px-3 py-2 text-sm font-mono"
                    placeholder={"[Encryption] AT-REST-1: Do you encrypt data at rest?\n[Encryption] IN-TRANSIT-1: Do you encrypt data in transit?\nDo you have a documented incident response plan?"}
                    value={bulk}
                    onChange={(e) => setBulk(e.target.value)}
                    data-testid="answer-q-detail-bulk-textarea"
                  />
                  <div className="flex items-center gap-3">
                    <Button
                      type="submit"
                      disabled={importMut.isPending || parseBulk(bulk).length === 0}
                      data-testid="answer-q-detail-import-cta"
                    >
                      {importMut.isPending
                        ? "Importing..."
                        : `Import ${parseBulk(bulk).length} question${
                            parseBulk(bulk).length === 1 ? "" : "s"
                          }`}
                    </Button>
                    {importMut.error && (
                      <span className="text-sm text-destructive">{importMut.error.message}</span>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {q.data.questions.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <p
                  className="text-sm text-muted-foreground"
                  data-testid="answer-q-detail-empty-questions"
                >
                  No questions yet. {q.data.status === "draft" ? "Import some above." : ""}
                </p>
              </CardContent>
            </Card>
          ) : (
            <ul className="flex flex-col gap-3" data-testid="answer-q-detail-questions-list">
              {q.data.questions.map((qq, idx) => (
                <QuestionWithResponseRow key={qq.id} question={qq} index={idx + 1} />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function QuestionWithResponseRow({
  question,
  index,
}: {
  question: AnswerQuestionWithResponse;
  index: number;
}) {
  const reviewMut = useReviewAnswerResponse();
  const promoteMut = usePromoteAnswerLibrary();

  const [finalText, setFinalText] = useState<string>(
    question.response?.final_text ?? question.response?.draft_text ?? "",
  );
  useEffect(() => {
    if (question.response) {
      setFinalText(question.response.final_text ?? question.response.draft_text ?? "");
    }
  }, [question.response]);

  function save(reviewStatus?: "approved" | "edited" | "rejected") {
    if (!question.response) return;
    reviewMut.mutate({
      id: question.response.id,
      body: {
        final_text: finalText,
        ...(reviewStatus ? { review_status: reviewStatus } : {}),
      },
    });
  }

  function promote() {
    if (!question.response) return;
    if (question.response.review_status !== "approved") {
      window.alert("Approve the response first, then promote to library.");
      return;
    }
    promoteMut.mutate({ source_response_id: question.response.id });
  }

  return (
    <li>
      <Card data-testid={`answer-q-detail-question-${index}`}>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Q{index}
                {question.external_id ? ` · ${question.external_id}` : ""}
                {question.section ? ` · ${question.section}` : ""}
              </p>
              <p className="mt-1 text-sm font-medium text-gray-900">{question.question_text}</p>
            </div>
            {question.response && (
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">{question.response.confidence}</Badge>
                <Badge
                  variant={
                    question.response.review_status === "approved" ? "default" : "outline"
                  }
                >
                  {question.response.review_status}
                </Badge>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {!question.response ? (
            <p className="text-sm text-muted-foreground">
              No draft yet. Click "Generate drafts" on the questionnaire header to produce one.
            </p>
          ) : (
            <>
              <textarea
                className="min-h-[100px] rounded border border-input bg-background px-3 py-2 text-sm"
                value={finalText}
                onChange={(e) => setFinalText(e.target.value)}
                placeholder="Edit the draft, then approve/edit/reject below."
                data-testid={`answer-q-detail-question-${index}-textarea`}
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => save("approved")}
                  disabled={
                    reviewMut.isPending || question.response.review_status === "approved"
                  }
                  data-testid={`answer-q-detail-question-${index}-approve`}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => save("edited")}
                  disabled={reviewMut.isPending}
                >
                  Save edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => save("rejected")}
                  disabled={
                    reviewMut.isPending || question.response.review_status === "rejected"
                  }
                >
                  Reject
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={promote}
                  disabled={
                    promoteMut.isPending || question.response.review_status !== "approved"
                  }
                  data-testid={`answer-q-detail-question-${index}-promote`}
                >
                  Promote to library
                </Button>
                {reviewMut.error && (
                  <span className="text-xs text-destructive">{reviewMut.error.message}</span>
                )}
                {promoteMut.error && (
                  <span className="text-xs text-destructive">{promoteMut.error.message}</span>
                )}
                {promoteMut.isSuccess && (
                  <span className="text-xs text-muted-foreground">Added to library.</span>
                )}
              </div>
              {question.citations.length > 0 && (
                <div className="rounded border border-gray-100 bg-gray-50/50 p-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Citations ({question.citations.length})
                  </p>
                  <ul className="flex flex-col gap-2 text-xs">
                    {question.citations.map((c) => (
                      <li key={c.id} className="text-gray-700">
                        <span className="font-medium">
                          {c.page_start
                            ? `p.${c.page_start}${
                                c.page_end && c.page_end !== c.page_start ? `-${c.page_end}` : ""
                              }`
                            : "source"}
                        </span>
                        : <span className="italic">"{c.snippet}"</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </li>
  );
}
